import { cx } from '@/utils/cx';

const styles = {
  '2xs': {
    root: 'gap-1',
    label: 'text-[10px] font-medium',
    spinner: 'size-3',
    dot: 'size-1',
    dotsGap: 'gap-0.5',
  },
  xs: {
    root: 'gap-2',
    label: 'text-xs font-medium',
    spinner: 'size-6',
    dot: 'size-1.5',
    dotsGap: 'gap-1',
  },
  sm: {
    root: 'gap-4',
    label: 'text-sm font-medium',
    spinner: 'size-8',
    dot: 'size-2',
    dotsGap: 'gap-1',
  },
  md: {
    root: 'gap-4',
    label: 'text-sm font-medium',
    spinner: 'size-12',
    dot: 'size-2.5',
    dotsGap: 'gap-1.5',
  },
  lg: {
    root: 'gap-4',
    label: 'text-lg font-medium',
    spinner: 'size-14',
    dot: 'size-3',
    dotsGap: 'gap-1.5',
  },
  xl: {
    root: 'gap-5',
    label: 'text-lg font-medium',
    spinner: 'size-16',
    dot: 'size-3.5',
    dotsGap: 'gap-2',
  },
};

interface LoadingIndicatorProps {
  /**
   * The visual style of the loading indicator.
   *
   * - `line-simple` / `line-spinner` / `dot-circle` — circular spinners
   * - `dots` — three bouncing dots (typing-indicator style). Uses `bg-current`
   *   so dot color is inherited from the parent's text color.
   * - `ahoy` — brand-gradient ring that rotates around a static Ahoy ship mark
   *   (the "Thinking…" loader). Brand-coloured; ignores `bg-current`.
   *
   * @default 'line-simple'
   */
  type?: 'line-simple' | 'line-spinner' | 'dot-circle' | 'dots' | 'ahoy';
  /**
   * The size of the loading indicator.
   * Options: '2xs' (12px), 'xs' (24px), 'sm' (32px), 'md' (48px), 'lg' (56px), 'xl' (64px)
   * @default 'sm'
   */
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Optional text label. Rendered below the spinner by default, or beside it
   * in `inline` mode.
   */
  label?: string;
  /**
   * Extra classes for the label, merged over the size-based defaults — lets
   * callers control the label's font/colour (e.g. an italic brand label).
   */
  labelClassName?: string;
  /**
   * Renders a horizontal spinner + label (label to the RIGHT of the spinner)
   * without the centering wrapper. When no label is passed it's just the
   * spinner. (Non-inline renders the label BELOW the spinner.)
   */
  inline?: boolean;
}

export const LoadingIndicator = ({
  type = 'line-simple',
  size = 'sm',
  label,
  labelClassName,
  inline = false,
}: LoadingIndicatorProps) => {
  let spinner: React.ReactNode;
  if (type === 'ahoy') {
    // Brand-gradient ring rotating around a static Ahoy ship mark. The ring
    // (track + gradient arc) spins as one SVG — the full-circle track hides its
    // own rotation, so only the gradient arc reads as motion; the logo overlay
    // stays put. Colors use design tokens rather than the source hex.
    spinner = (
      <div className={cx('relative', styles[size].spinner)} aria-hidden='true'>
        <svg
          className='absolute inset-0 size-full motion-safe:animate-spin'
          viewBox='0 0 40 40'
          fill='none'
        >
          <path
            d='M37.5 20C37.5 22.2981 37.0474 24.5738 36.1679 26.697C35.2884 28.8202 33.9994 30.7493 32.3744 32.3744C30.7493 33.9994 28.8202 35.2884 26.697 36.1679C24.5738 37.0473 22.2981 37.5 20 37.5C17.7019 37.5 15.4262 37.0473 13.303 36.1679C11.1798 35.2884 9.25065 33.9994 7.62563 32.3744C6.0006 30.7493 4.71156 28.8202 3.83211 26.697C2.95265 24.5738 2.5 22.2981 2.5 20C2.5 17.7019 2.95265 15.4262 3.83211 13.303C4.71157 11.1798 6.00061 9.25065 7.62564 7.62563C9.25066 6.0006 11.1799 4.71156 13.303 3.83211C15.4262 2.95265 17.7019 2.5 20 2.5C22.2981 2.5 24.5738 2.95265 26.697 3.83211C28.8202 4.71157 30.7494 6.00061 32.3744 7.62564C33.9994 9.25066 35.2884 11.1799 36.1679 13.303C37.0474 15.4262 37.5 17.7019 37.5 20L37.5 20Z'
            stroke='var(--color-border-secondary)'
            strokeWidth='4'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M7.62563 7.62563C9.57844 5.67283 11.9657 4.20992 14.5922 3.35651C17.2187 2.5031 20.0099 2.28343 22.7376 2.71545C25.4653 3.14748 28.052 4.21893 30.2863 5.8422C32.5205 7.46548 34.3388 9.59449 35.5926 12.0552C36.8464 14.5158 37.5 17.2383 37.5 20C37.5 22.7617 36.8464 25.4842 35.5926 27.9448C34.3388 30.4055 32.5205 32.5345 30.2862 34.1578C28.052 35.7811 25.4653 36.8525 22.7376 37.2845'
            stroke='url(#ahoy-loader-gradient)'
            strokeWidth='4'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <defs>
            <linearGradient
              id='ahoy-loader-gradient'
              x1='20'
              y1='2.5'
              x2='20'
              y2='37.5'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='var(--color-fg-brand-primary)' />
              <stop
                offset='1'
                stopColor='var(--color-fg-brand-primary)'
                stopOpacity='0'
              />
            </linearGradient>
          </defs>
        </svg>
        <svg
          className='absolute inset-0 size-full text-[var(--color-fg-brand-primary)]'
          viewBox='0 0 40 40'
          fill='currentColor'
        >
          <path d='M24.7706 15.8021C24.2472 15.6203 23.4881 15.0191 22.84 14.5017C22.4824 14.2162 21.9577 14.5537 22.0079 15.0085C22.0619 15.4988 22.1067 16.0393 22.129 16.4784C22.1476 16.8462 22.5374 17.1431 22.8885 17.0317L24.8488 16.4094C24.9663 16.3721 25.0461 16.2664 25.0461 16.1431C25.0461 15.9869 24.9182 15.8533 24.7706 15.8021Z' />
          <path d='M21.0331 17.5687L19.6314 17.9544C19.2974 18.0463 18.9739 17.7781 18.9751 17.4317C18.981 15.7794 18.4993 14.77 17.7448 14.0313C17.4257 13.719 17.5423 13.1436 17.9884 13.124C21.1053 12.9876 21.6222 15.5972 21.3764 17.1973C21.3485 17.3789 21.2102 17.52 21.0331 17.5687Z' />
          <path d='M17.7127 18.5007L16.237 18.9861C15.8891 19.1006 15.5396 18.813 15.5516 18.4469C15.609 16.7022 15.0562 15.6928 14.3468 14.7653C14.1279 14.4791 14.2599 14.0591 14.6153 14.0001C18.0867 13.4236 18.313 16.61 18.0311 18.1481C18.0003 18.3161 17.875 18.4473 17.7127 18.5007Z' />
          <path d='M12.9462 21.1683L12.7266 20.777C12.5675 20.4932 12.7174 20.1354 13.0312 20.0499L14.0807 19.7639C14.2062 19.7297 14.3401 19.7458 14.4539 19.8086L15.0078 20.1145C15.1278 20.1808 15.2698 20.1949 15.4005 20.1535L27.5366 16.3107C27.7477 16.2439 27.9629 16.4015 27.9629 16.6229C27.9629 16.7853 27.8452 16.9314 27.691 16.9825C27.4052 17.077 26.8949 17.2687 26.0814 17.6176C25.8987 17.696 25.7808 17.8773 25.7794 18.0761C25.767 19.8624 25.6337 20.7375 24.8184 21.3517C24.5963 21.5191 24.2882 21.4125 24.0964 21.2111C23.2466 20.3188 21.4606 20.0767 20.6518 20.4429C19.2878 21.0605 18.8513 22.0126 17.3236 23.3601C15.5044 24.5958 14.5169 23.0707 14.2387 21.8785C14.1636 21.5568 13.8364 21.317 13.5181 21.4048C13.2964 21.4659 13.0587 21.3688 12.9462 21.1683Z' />
          <path d='M20.4417 24.5416C21.4488 27.0634 24.3774 25.0135 24.6008 23.3601C24.8109 21.8048 25.5464 21.6196 26.8171 23.6994C27.7583 25.2398 26.8919 26.619 25.9987 26.619H14.9348C12.4132 26.619 13.5636 23.4738 14.1127 23.9825C15.0398 24.8414 15.2442 24.9525 16.1956 24.9525C16.826 24.9525 17.7715 24.397 18.9605 23.0818C20.1494 21.7665 20.5032 21.6196 21.1336 21.3974C21.764 21.1752 23.6115 21.2065 23.5981 22.9133C23.5846 24.6202 22.0228 25.035 21.6804 23.9801C21.2387 22.6195 19.9779 23.6194 20.4417 24.5416Z' />
          <path d='M25.7759 10.4283L25.9051 11.251C26.0208 11.9868 26.5708 12.5635 27.2727 12.6847L28.0574 12.8202L27.2727 12.9557C26.5708 13.0769 26.0208 13.6536 25.9051 14.3894L25.7759 15.212L25.6466 14.3894C25.531 13.6536 24.9809 13.0769 24.2791 12.9557L23.4944 12.8202L24.2791 12.6847C24.9809 12.5635 25.531 11.9868 25.6466 11.251L25.7759 10.4283Z' />
        </svg>
      </div>
    );
  } else if (type === 'line-spinner') {
    spinner = (
      <svg
        className={cx('animate-spin', styles[size].spinner)}
        viewBox='0 0 32 32'
        fill='none'
      >
        <circle
          cx='16'
          cy='16'
          r='14'
          fill='none'
          stroke='var(--color-fg-brand-primary)'
          strokeWidth='4'
          strokeDashoffset='40'
          strokeDasharray='100'
          strokeLinecap='round'
        />
      </svg>
    );
  } else if (type === 'dots') {
    spinner = (
      <span
        className={cx('inline-flex items-center', styles[size].dotsGap)}
        aria-hidden='true'
      >
        <span
          className={cx(
            styles[size].dot,
            'rounded-full bg-current animate-bounce [animation-delay:-0.3s]'
          )}
        />
        <span
          className={cx(
            styles[size].dot,
            'rounded-full bg-current animate-bounce [animation-delay:-0.15s]'
          )}
        />
        <span
          className={cx(
            styles[size].dot,
            'rounded-full bg-current animate-bounce'
          )}
        />
      </span>
    );
  } else if (type === 'dot-circle') {
    spinner = (
      <svg
        className={cx(
          'animate-spin text-[var(--color-fg-brand-primary)]',
          styles[size].spinner
        )}
        viewBox='0 0 36 36'
        fill='none'
      >
        <path
          d='M34 18C34 15.8989 33.5861 13.8183 32.7821 11.8771C31.978 9.93586 30.7994 8.17203 29.3137 6.68629C27.828 5.20055 26.0641 4.022 24.1229 3.21793C22.1817 2.41385 20.1011 2 18 2C15.8988 2 13.8183 2.41385 11.8771 3.21793C9.93585 4.022 8.17203 5.20055 6.68629 6.68629C5.20055 8.17203 4.022 9.93586 3.21793 11.8771C2.41385 13.8183 2 15.8989 2 18'
          stroke='url(#paint0)'
          strokeWidth='4'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeDasharray='0.1 8'
        />
        <path
          d='M3.21793 24.1229C4.022 26.0641 5.20055 27.828 6.68629 29.3137C8.17203 30.7994 9.93585 31.978 11.8771 32.7821C13.8183 33.5861 15.8988 34 18 34C20.1011 34 22.1817 33.5861 24.1229 32.7821C26.0641 31.978 27.828 30.7994 29.3137 29.3137C30.7994 27.828 31.978 26.0641 32.7821 24.1229'
          stroke='url(#paint1)'
          strokeWidth='4'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeDasharray='0.1 8'
        />
        <defs>
          <linearGradient
            id='paint0'
            x1='34'
            y1='18'
            x2='2'
            y2='18'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopColor='currentColor' />
            <stop offset='1' stopColor='currentColor' stopOpacity='0.5' />
          </linearGradient>
          <linearGradient
            id='paint1'
            x1='33'
            y1='23.5'
            x2='3'
            y2='24'
            gradientUnits='userSpaceOnUse'
          >
            <stop stopOpacity='0' stopColor='currentColor' />
            <stop offset='1' stopColor='currentColor' stopOpacity='0.48' />
          </linearGradient>
        </defs>
      </svg>
    );
  } else {
    // Default: type === "line-simple"
    spinner = (
      <svg
        className={cx('animate-spin', styles[size].spinner)}
        viewBox='0 0 32 32'
        fill='none'
      >
        <circle
          cx='16'
          cy='16'
          r='14'
          stroke='var(--color-bg-tertiary)'
          strokeWidth='4'
        />
        <circle
          cx='16'
          cy='16'
          r='14'
          fill='none'
          stroke='var(--color-fg-brand-primary)'
          strokeWidth='4'
          strokeDashoffset='75'
          strokeDasharray='100'
          strokeLinecap='round'
        />
      </svg>
    );
  }

  if (inline) {
    return (
      <span
        role='progressbar'
        aria-label={label || 'Loading'}
        className='inline-flex items-center gap-3'
      >
        {spinner}
        {label && (
          <span
            className={cx(
              styles[size].label,
              // Only apply the default colour when the caller hasn't supplied
              // its own — two arbitrary `text-[var(--color-*)]` classes can't be
              // de-duped by tailwind-merge, so keeping both lets CSS order win.
              labelClassName ?? 'text-[var(--color-text-secondary)]'
            )}
          >
            {label}
          </span>
        )}
      </span>
    );
  }

  return (
    <div
      role='progressbar'
      aria-label={label || 'Loading'}
      className={cx(
        'loading-indicator flex flex-col items-center justify-center',
        styles[size].root
      )}
    >
      {spinner}
      {label && (
        <span
          className={cx(
            styles[size].label,
            labelClassName ?? 'text-[var(--color-text-secondary)]'
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
};
